/*
 * Local-mode JSON CRUD on top of localStorage. Each kind keyed by episode
 * uuid so a single browser can hold many local episodes.
 */

import type {
  Character,
  Episode,
  PromptTemplate,
  Scene,
  SceneImage,
  SceneVideo,
  StylePreset,
  StyleSettings,
} from '@/types';

const GLOBAL_KEY = '__global__';

type Kind =
  | 'episodes'
  | 'scenes'
  | 'images'
  | 'videos'
  | 'characters'
  | 'style-settings'
  | 'style-presets'
  | 'prompt-templates'
  | 'app-settings';

const PREFIX = 'studio-v2.';

/**
 * Bumped whenever the localStorage shape changes incompatibly. Old data
 * doesn't migrate cleanly across the two-slot template rewrite (PromptTemplate
 * gained `kind`/`isSystem`, StyleSettings replaced `defaultPromptTemplateId`
 * with two new fields), so we drop and reseed on mismatch. Episode media
 * blobs in IDB survive — only the JSON metadata gets nuked.
 */
const SCHEMA_VERSION = 2;
const SCHEMA_KEY = `${PREFIX}schema-version`;

function key(kind: Kind, uuid: string): string {
  return `${PREFIX}${kind}.${uuid}`;
}

function read<T>(k: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(k);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function write<T>(k: string, value: T): void {
  localStorage.setItem(k, JSON.stringify(value));
}

export const localStore = {
  getEpisode(uuid: string): Episode | null {
    return read<Episode | null>(key('episodes', uuid), null);
  },
  putEpisode(episode: Episode): void {
    write(key('episodes', episode.uuid), episode);
  },

  getScenes(uuid: string): Scene[] {
    return read<Scene[]>(key('scenes', uuid), []);
  },
  putScenes(uuid: string, scenes: Scene[]): void {
    write(key('scenes', uuid), scenes);
  },

  getImages(uuid: string): SceneImage[] {
    return read<SceneImage[]>(key('images', uuid), []);
  },
  putImages(uuid: string, images: SceneImage[]): void {
    write(key('images', uuid), images);
  },

  getVideos(uuid: string): SceneVideo[] {
    return read<SceneVideo[]>(key('videos', uuid), []);
  },
  putVideos(uuid: string, videos: SceneVideo[]): void {
    write(key('videos', uuid), videos);
  },

  getCharacters(uuid: string): Character[] {
    return read<Character[]>(key('characters', uuid), []);
  },
  putCharacters(uuid: string, characters: Character[]): void {
    write(key('characters', uuid), characters);
  },

  getStyleSettings(uuid: string): StyleSettings | null {
    return read<StyleSettings | null>(key('style-settings', uuid), null);
  },
  putStyleSettings(uuid: string, settings: StyleSettings): void {
    write(key('style-settings', uuid), settings);
  },

  getStylePresets(): StylePreset[] {
    return read<StylePreset[]>(key('style-presets', GLOBAL_KEY), []);
  },
  putStylePresets(presets: StylePreset[]): void {
    write(key('style-presets', GLOBAL_KEY), presets);
  },

  getPromptTemplates(uuid: string): PromptTemplate[] {
    const global = read<PromptTemplate[]>(key('prompt-templates', GLOBAL_KEY), []);
    const episode = read<PromptTemplate[]>(key('prompt-templates', uuid), []);
    return [...global, ...episode];
  },
  putGlobalPromptTemplates(templates: PromptTemplate[]): void {
    write(key('prompt-templates', GLOBAL_KEY), templates);
  },
  putEpisodePromptTemplates(uuid: string, templates: PromptTemplate[]): void {
    write(key('prompt-templates', uuid), templates);
  },

  clearAll(): void {
    const keysToDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) keysToDelete.push(k);
    }
    for (const k of keysToDelete) localStorage.removeItem(k);
  },

  exportAll(): Record<string, unknown> {
    const dump: Record<string, unknown> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) {
        dump[k] = JSON.parse(localStorage.getItem(k) ?? 'null');
      }
    }
    return dump;
  },

  importAll(dump: Record<string, unknown>): void {
    localStore.clearAll();
    for (const [k, v] of Object.entries(dump)) {
      if (!k.startsWith(PREFIX)) continue;
      localStorage.setItem(k, JSON.stringify(v));
    }
  },

  // ─── Schema migration (v1 → v2: two-slot templates) ───
  //
  // Called once on first localBackend access per session. If the stored
  // version is missing or older than SCHEMA_VERSION, wipe the metadata
  // blobs that hold incompatible shapes (templates + style-settings) and
  // let ensureDefaultsSeeded() reseed. We deliberately keep scenes/images/
  // videos/characters intact — those types didn't change.
  ensureSchemaMigrated(): void {
    if (typeof window === 'undefined') return;
    const current = (() => {
      try { return Number(localStorage.getItem(SCHEMA_KEY) ?? '0'); }
      catch { return 0; }
    })();
    if (current >= SCHEMA_VERSION) return;

    const keysToDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (
        k.startsWith(`${PREFIX}prompt-templates.`) ||
        k.startsWith(`${PREFIX}style-presets.`)
      ) {
        keysToDelete.push(k);
      }
    }
    for (const k of keysToDelete) localStorage.removeItem(k);

    // StyleSettings blobs need the renamed fields. Rather than per-row
    // migration, drop the field if present and let the backend fill in the
    // new defaults on next read.
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(`${PREFIX}style-settings.`)) continue;
      try {
        const obj = JSON.parse(localStorage.getItem(k) ?? 'null');
        if (obj && typeof obj === 'object' && 'defaultPromptTemplateId' in obj) {
          delete obj.defaultPromptTemplateId;
          obj.activeImageTemplateId = obj.activeImageTemplateId ?? null;
          obj.activeVideoTemplateId = obj.activeVideoTemplateId ?? null;
          localStorage.setItem(k, JSON.stringify(obj));
        }
      } catch { /* corrupt blob — leave it; next read will fall back to defaults */ }
    }

    try { localStorage.setItem(SCHEMA_KEY, String(SCHEMA_VERSION)); }
    catch { /* storage full — best effort, will retry next session */ }
  },

  // ─── App-wide key/value (today: vertex model selection) ───
  //
  // Mirror of the backend's `app_settings` table for local mode. Keyed by
  // setting name (e.g. 'vertex.model.image').
  getAppSetting(key: string): string | null {
    return read<string | null>(`${PREFIX}app-settings.${key}`, null);
  },
  putAppSetting(key: string, value: string): void {
    write(`${PREFIX}app-settings.${key}`, value);
  },
};
